/**
 * Proportional falloff and bilateral mirror pairing — the two options that turn
 * `moveCorners` from a per-corner nudge into a shaping operation.
 *
 * DISTANCE IS ACCUMULATED EDGE LENGTH, NOT HOP COUNT. Topology density varies
 * across one mesh, so a hop radius produces a large blend in a coarse region and
 * a tiny one in a dense region of the same model — exactly the failure a
 * proportional edit must not have. Lengths accumulate in OBJECT-LOCAL space,
 * the space topology positions live in and the space a relative `delta` is
 * expressed in by default, so a radius and a displacement share units. Under a
 * non-uniform object scale that is not world distance; that is the deliberate
 * trade, and it is why `radius` is documented in local units at the command.
 *
 * THE RETURNED MAP CONTAINS ONLY CORNERS THAT ACTUALLY MOVE — every entry has
 * weight > 0, and anything at or beyond `radius` is absent rather than present
 * with a zero. A caller can therefore iterate the map directly without having
 * to remember to skip zeros, and cannot accidentally write an unchanged
 * position back over a corner it never meant to touch.
 */

import { sculptEdgeKey } from './sculpt-neighbors.js';

const CURVES = {
  // Falls to zero at the radius; each is 1 at t=0 and 0 at t=1.
  constant: () => 1,
  linear: (t) => 1 - t,
  smooth: (t) => 1 - (t * t * (3 - 2 * t)),
  sharp: (t) => (1 - t) ** 2,
};

export const FALLOFF_CURVES = Object.keys(CURVES);

/**
 * Multi-source shortest-path weights over the sculpt-neighbour graph.
 *
 * `graph` is `buildSculptGraph(...)`. Every seed starts at distance 0 with
 * weight 1. Ties are broken by ascending corner id so two runs over the same
 * inputs — and two viewers applying the same command — settle identically.
 *
 * A plain scan-for-minimum is used rather than a binary heap: the frontier is
 * bounded by the corners within `radius`, which for a shaping edit is a small
 * neighbourhood, and a deterministic tie-break is trivial to state this way.
 */
export function falloffWeights(graph, seedCornerIds, { radius, curve = 'smooth' } = {}) {
  if (!graph || !(graph.nodes instanceof Map)) {
    throw new Error('falloffWeights requires a sculpt graph with a nodes map');
  }
  if (typeof radius !== 'number' || !Number.isFinite(radius) || radius <= 0) {
    throw new Error(`falloff radius must be a finite number > 0, got ${JSON.stringify(radius)}`);
  }
  const shape = CURVES[curve];
  if (!shape) {
    throw new Error(`falloff curve must be one of ${FALLOFF_CURVES.join(', ')}, got ${JSON.stringify(curve)}`);
  }
  const seeds = [...new Set(seedCornerIds)];
  for (const id of seeds) {
    if (!graph.nodes.has(id)) throw new Error(`falloffWeights: unknown corner id '${id}'`);
  }

  const distance = new Map(seeds.map((id) => [id, 0]));
  const settled = new Set();

  for (;;) {
    let current = null;
    let best = Infinity;
    for (const [id, d] of distance) {
      if (settled.has(id)) continue;
      // Strict `<` plus an id comparison on equality: without the tie-break,
      // Map iteration order would decide, and that follows insertion order,
      // which follows discovery order, which is not a property callers should
      // be able to observe.
      if (d < best || (d === best && current !== null && id < current)) {
        best = d;
        current = id;
      }
    }
    if (current === null || best >= radius) break;
    settled.add(current);

    for (const neighbour of graph.nodes.get(current)) {
      if (settled.has(neighbour)) continue;
      const step = graph.edgeLength.get(sculptEdgeKey(current, neighbour));
      if (step === undefined) {
        // The graph promises a length for every edge in `nodes`, so a miss is a
        // malformed graph, not a corner that happens to be far away. Silently
        // skipping it would make falloff quietly reach nothing — which is
        // exactly what an earlier version of this file did, by rebuilding the
        // key format locally instead of importing it.
        throw new Error(`falloffWeights: sculpt graph has no length for edge ${current} - ${neighbour}`);
      }
      const candidate = best + step;
      if (candidate >= radius) continue;
      const known = distance.get(neighbour);
      if (known === undefined || candidate < known) distance.set(neighbour, candidate);
    }
  }

  const weights = new Map();
  for (const id of [...settled].sort()) {
    weights.set(id, shape(distance.get(id) / radius));
  }
  return weights;
}

/**
 * Pair corners across a mirror plane by position.
 *
 * Returns `{ pairs, onPlane, unmatched }`:
 *   pairs     Map<cornerId, cornerId>  each matched corner to its reflection
 *   onPlane   corner ids lying on the plane itself, which mirror to themselves
 *   unmatched corner ids with no partner within `tolerance`
 *
 * `unmatched` is the load-bearing output. It is how a caller learns the mesh is
 * not actually symmetric BEFORE trusting a symmetric edit — a mirror option
 * that silently did nothing on the unmatched half would produce exactly the
 * asymmetric drift the option exists to prevent. `moveCorners` reports it and
 * still applies the move to the matched corners: a warning, not a failure.
 *
 * Candidates are found through a spatial hash at `tolerance` granularity and
 * then confirmed by true distance, so matching stays linear in corner count
 * while the tolerance boundary remains exact rather than quantised.
 */
export function mirrorPairs(corners, { axis, about = 0, tolerance = 1e-6 } = {}) {
  const axisIndex = { x: 0, y: 1, z: 2 }[axis];
  if (axisIndex === undefined) {
    throw new Error(`mirror axis must be "x", "y" or "z", got ${JSON.stringify(axis)}`);
  }
  if (typeof about !== 'number' || !Number.isFinite(about)) {
    throw new Error('mirror about must be a finite number');
  }
  if (typeof tolerance !== 'number' || !Number.isFinite(tolerance) || tolerance < 0) {
    throw new Error('mirror tolerance must be a finite number >= 0');
  }
  if (!Array.isArray(corners)) throw new Error('mirrorPairs requires the corners array');

  // Bucket size, floored well above zero. `Math.max(tolerance, MIN_VALUE)`
  // looks safe but is not: at `tolerance: 0` the cell becomes 5e-324, every
  // `Math.round(p / cell)` overflows to Infinity, and the whole corner set
  // lands in a single bucket — still correct, because the true-distance check
  // below is what actually decides, but the spatial hash stops helping and
  // matching silently degrades to O(n²). A 1e-9 floor keeps buckets finite for
  // any coordinate magnitude the renderer can represent, and exact matching at
  // `tolerance: 0` still works because neighbouring cells are scanned.
  const cell = Math.max(tolerance, 1e-9);
  const keyOf = (p) => `${Math.round(p[0] / cell)} ${Math.round(p[1] / cell)} ${Math.round(p[2] / cell)}`;
  const buckets = new Map();
  for (const corner of corners) {
    const key = keyOf(corner.position);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(corner);
    else buckets.set(key, [corner]);
  }

  const reflect = (position) => {
    const mirrored = [...position];
    mirrored[axisIndex] = 2 * about - position[axisIndex];
    return mirrored;
  };
  const within = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) <= tolerance;

  const pairs = new Map();
  const onPlane = [];
  const unmatched = [];
  const ambiguous = [];

  // Pass 1 — every candidate within tolerance, not merely the first one found.
  // Stopping at the first match is what let two corners silently claim the same
  // partner while `unmatched` stayed empty: the caller then believed the mesh
  // was symmetric and the second corner's reflection quietly overwrote the
  // first's.
  const candidatesFor = new Map();
  for (const corner of corners) {
    const target = reflect(corner.position);
    if (within(corner.position, target)) {
      onPlane.push(corner.id);
      continue;
    }
    const base = [
      Math.round(target[0] / cell),
      Math.round(target[1] / cell),
      Math.round(target[2] / cell),
    ];
    const found = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          for (const candidate of buckets.get(`${base[0] + dx} ${base[1] + dy} ${base[2] + dz}`) ?? []) {
            if (candidate.id !== corner.id && within(candidate.position, target)) found.push(candidate.id);
          }
        }
      }
    }
    candidatesFor.set(corner.id, [...new Set(found)].sort());
  }

  // Pass 2 — classify. More than one candidate is genuinely ambiguous: picking
  // the lexicographically smallest would be deterministic but arbitrary, and a
  // symmetric edit built on an arbitrary pick is worse than one the caller
  // knows it cannot make.
  const tentative = new Map();
  for (const [cornerId, candidates] of candidatesFor) {
    if (candidates.length === 0) unmatched.push(cornerId);
    else if (candidates.length > 1) ambiguous.push(cornerId);
    else tentative.set(cornerId, candidates[0]);
  }

  // Pass 3 — keep only MUTUAL matches, which is what makes the result both
  // reciprocal and one-to-one. If A's only candidate is B but B's is not A
  // (because B is ambiguous, unmatched, or claimed by someone nearer), the
  // relationship is not a mirror pair and A is reported as ambiguous rather
  // than paired to a partner that does not agree.
  for (const [cornerId, partner] of tentative) {
    if (tentative.get(partner) === cornerId) pairs.set(cornerId, partner);
    else ambiguous.push(cornerId);
  }

  return {
    pairs,
    onPlane: onPlane.sort(),
    unmatched: unmatched.sort(),
    ambiguous: ambiguous.sort(),
  };
}
